import { ComposeExtensionsType } from '../AppCatalog/appDefinitionModel';
import { AppEligibilityInformation } from '../meta-os-app-container.interface';
import { FailedReason } from './constants';
import { IWindowProvider } from './event-listener.interface';
import { AppTerminationReason } from './interfaces';
import {
  AvailableHubNames,
  FrameContexts,
  HostClientType,
  HubNames,
  IFrameInfo,
  ILocaleInfo,
  ISerializedWebContentRequestMessage,
  ISerializedWebContentResponseMessage,
  IWebContentMessageEvent,
  IWebContentRequestAdditionalInfo,
  IWebContentRequestMessage,
  RenderingSurfaces,
  Themes,
} from './messages.interface';
import { HostFeatures } from './utils/hostFeatures';
import { UUID } from './utils/uuidObject';

/**
 * The CustomEngineAgent type.
 */
export type CEAType = 'bot';

/**
 * Information about the app required for the Hub SDK to host it.
 *
 * It's very much by design that it isn't like the data returned from MT or MOS3 services.
 */
export interface AppDefinition {
  /**
   * Represents the app's eligibility information.
   */
  appEligibilityInformation?: AppEligibilityInformation;
  composeExtension?: {
    botId: string;
    type?: ComposeExtensionsType;
    commands: AppDefinitionCommand[];
  };
  copilotAgents?: {
    customEngineAgents: ReadonlyArray<{
      id: UUID;
      type: CEAType;
    }>;
  };
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  plugins?: {};
  connector?: {
    configurationUrl?: string;
    connectorId: string;
    scopes: ReadonlyArray<string>;
  };
  devicePermissions: ReadonlyArray<string>;
  id: string;

  /**
   * @deprecated Use {@link AppDefinition.isMicrosoftOwned} instead.
   */
  isFullTrust?: boolean;

  isLOB: boolean;
  isMicrosoftOwned?: boolean;
  manifestVersion: string;
  pages: {
    configurable: ReadonlyArray<{ configurationUrl: string }>;
    static: ReadonlyArray<{
      entityId: string;
      contentUrl?: string;
      name?: string;
    }>;
  };
  showLoadingIndicator: boolean;
  validDomains: ReadonlyArray<string>;
  version: string;
  webApplicationInfo?: {
    id: string;
    resource?: string;
  };
}
export interface AppDefinitionCommand {
  id: string;
  parameterNames: string[];
}
/**
 * Interface for Hosted web content sdk service
 */
export interface IHostedWebContentAuthSdkService {
  /**
   * is authorization window open
   */
  isWindowOpen(): boolean;

  /**
   * to send request message to auth frame
   * @param functionName: function name
   * @param funArguments: function arguments
   */
  sendRequestMessage(func: string, args?: (object | string)[]): void;

  /**
   * close authorization window
   */
  closeWindow(): void;
}

/**
 * Interface for Hosted web content external app auth service
 */
export interface IHostedWebContentExternalAppAuthService extends IHostedWebContentAuthSdkService {
  /**
   * launch window open
   */
  launchAuthWindow(): void;
}
/**
 * Interface for Hosted web content sdk service
 */
export interface IHostedWebContentSdkService {
  /**
   * Returns a boolean indicating whether the app is being launched from cache.
   * @returns {boolean} True if the app is launching from cache, otherwise false.
   */
  getIsCachedLaunch(): boolean;

  /**
   * initialize frame and load target origin to frame
   * @param frame target frame
   * @param origin target origin
   * @param appDefinition Hosted web content app definition
   * @param userClickTime point of time when the user clicked on the app in milli seconds
   * @param usingCachedApp the flag to indicate if the app is being loaded from cache (if launched from a cachedIframe, this value is set to true)
   * @param userClickTimeV2 point of time when the user clicked on the app in milli seconds using a monotonic timer
   * @param isSSREnabled - Optional flag to use form submission (POST) with authentication token instead of direct iframe src loading (GET)
   */
  setFrameAndLoadOrigin(
    frame: IFrameInfo | undefined | null,
    origin: string,
    appDefinition: AppDefinition,
    userClickTime: number,
    usingCachedApp?: boolean,
    userClickTimeV2?: number
  ): Promise<void>;

  /**
   * to send request message to frame
   * @param functionName: function name
   * @param args: function arguments
   */
  sendRequestMessage(functionName: string, args?: (object | string)[]): void;

  /**
   * to send a message to frame
   * @param message: message to send
   */
  postMessageToFrame(message: any): void;

  /**
   * Returns an origin the frame has been initialized with
   */
  getOrigin(): string | undefined;

  /**
   * Clear the messageProcessors when the user navigates away from the app
   * In case of caching, it will send the beforeUnload event to the app
   * @param appDefinition the app that needs to be unloaded
   */
  unloadApp(appDefinition: AppDefinition, reason?: AppTerminationReason): void;
}

export interface IMessageEventWithAsyncResponseHandler extends MessageEvent {
  /**
   * send async response to app
   */
  sendAsyncResponse?: (sdkRequestMessage: IWebContentRequestMessage) => void;
}

/**
 * Communication bridge to facilitate message passing between Hub SDK used by the host
 * and App SDK used by the hosted application.
 */
export interface ICommunicationBridge {
  /**
   * Unique Id of the communication bridge that should change every time the bridge is created new
   */
  readonly id?: UUID;
  /**
   * Returns the origin of the hosted app
   */
  getOrigin(): string | undefined;

  /**
   * Sets the origin of the hosted app
   */
  setOrigin: (value: string, isCached?: boolean) => void;

  /**
   * Called by SDK on initialization
   * @param hostWindow window of the Host
   */
  onSdkInitialized?: (hostWindow: Window | IWindowProvider) => void;

  /**
   * Called by SDK whenever the App url is changed
   * @param frame Frame where app should be loaded. It abstracts common functionalities required by frame types and hence, is expected to be used for iFrames, Electron WebView, WebView2 control
   * @param origin Content url of the launch page of the app which is being loaded
   * @param appDefinition AppDefinition of the app
   * @param handleMessageCallback Callback which handles the messages from Apps. Messages received from apps should be routed to this callback for Hubs to handle it
   */
  onSetFrameAndLoadOrigin: (
    frame: IFrameInfo,
    origin: string,
    appDefinition: AppDefinition,
    appContext: AppContext,
    handleMessageCallback: (event: IWebContentMessageEvent) => void,
    isCached?: boolean
  ) => void;

  /**
   * Called by SDK to post message to the App Window
   * @param target Frame where app should be loaded. It abstracts common functionalities required by frame types and hence, is expected to be used for iFrames, Electron WebView, WebView2 control
   * @param message Message to be sent to the app
   */
  postMessage: (
    target: IFrameInfo,
    message: ISerializedWebContentRequestMessage | ISerializedWebContentResponseMessage
  ) => void;

  /**
   * Called by SDK to post message with one or more transferable objects to the App Window
   * @param target Frame where app should be loaded. It abstracts common functionalities required by frame types and hence, is expected to be used for iFrames, Electron WebView, WebView2 control
   * @param message Message to be sent to the app
   */
  postMessageWithTransferables: (
    target: IFrameInfo,
    message: ISerializedWebContentRequestMessage | ISerializedWebContentResponseMessage,
    transferables: Transferable[]
  ) => void;

  /**
   * Called by SDK to post message to the nested iframe of the App Window
   * @param message Message to be sent to the app
   * @param requestInfo Links to {@link IWebContentRequestAdditionalInfo}.
   */
  postMessageToNestedIframe: (
    message: ISerializedWebContentRequestMessage | ISerializedWebContentResponseMessage,
    requestInfo: IWebContentRequestAdditionalInfo
  ) => void;

  /**
   * Called by SDK to dispose the resources used by communication bridge
   */
  dispose: () => void;
}

export type AppInitInfo =
  | {
      state:
        | AppInitState.Unknown
        | AppInitState.Loading
        | AppInitState.WaitingForAppLoad
        | AppInitState.Loaded
        | AppInitState.Cached
        | AppInitState.Unloaded
        | AppInitState.Unloading;
      appSdkVersion?: string;
      isInPrecachePhase?: boolean;
    }
  | AppInitInfoFailed;
export type AppInitInfoFailed = {
  state: AppInitState.Failed;
  reason: FailedReason;
  appSdkVersion?: string;
  /**
   * The value of the `www-authenticate` header from the server response.
   * WARNING: May contain sensitive authentication information. Do not log to telemetry.
   */
  authHeader?: string;
};

/**
 * Used to track the app initialization state of the MetaOS App hosted
 */
export enum AppInitState {
  Unknown = 'unknown',

  /**
   * Set when beginning the loading process, before setting the iframe URL
   */
  Loading = 'loading',
  /**
   * Set when  we have "loaded" the App - set the webview src or sent it the load signal
   */
  WaitingForAppLoad = 'WaitingForAppLoad',

  /**
   * Used to hide the Hub loading UI
   * IF the native loading indicator is being used, set in response to appInitialization.appLoaded and appInitialization.success
   * Otherwise, set immediately after creating the iFrame
   */
  Loaded = 'loaded',

  /**
   * Used to show the Hub error UI
   * If the native loading indicator is being used, set in response to appInitialization.failure,
   * if the appInitialization.success wasn't received after 30s,
   * if beforeUnload event was sent to app but readyToUnload event wasn't received from app after UNLOADING_TIMEOUT_MS (see packages/metaos-hub-sdk/src/Services/defaults/defaultCacheManagementService.ts),
   * or if no Initialize message received after 60s
   */
  Failed = 'failed',

  /**
   * Applies to Hubs that opt into Caching
   * Only applicable if Hub passes CacheManagementService implementation and "shouldTriggerUnload" returns true
   * Indicates that the SDK has sent the beforeUnload event and is waiting for the readyToUnload event from App
   */
  Unloading = 'unloading',

  /**
   * Applies to Hubs that opt into Caching
   * This indicates that the Hub has received the "readyToUnload" event from the App
   */
  Unloaded = 'unloaded',

  /**
   * Applies to Hubs that opt into Caching
   * This indicates that the App has transitioned to the Cached state
   */
  Cached = 'cached',
}

export interface ExternalAppError {
  errorCode: ExternalAppErrorType;
  message: string;
}

export enum ExternalAppErrorType {
  INTERNAL_ERROR = 'INTERNAL_ERROR', // Generic error
}

/**
 * Indicates the team type, currently used to distinguish between different team
 * types in Office 365 for Education (team types 1, 2, 3, and 4).
 */
export enum TeamType {
  Standard = 0,
  Edu = 1,
  Class = 2,
  Plc = 3,
  Staff = 4,
}

/**
 * The type of the channel with which the content is associated.
 */
export enum ChannelType {
  Regular = 'Regular',
  Private = 'Private',
  Shared = 'Shared',
}

/**
 * Indicates the various types of roles of a user in a team.
 */
export enum UserTeamRole {
  Admin = 0,
  User = 1,
  Guest = 2,
}

/**
 * Allowed user file open preferences
 */
export enum FileOpenPreference {
  Inline = 'inline',
  Desktop = 'desktop',
  Web = 'web',
}

export interface ContextBridge {
  /**
   * Common information applicable to all content actions
   */
  actionInfo?: ActionInfo;
  /**
   * The Office 365 group ID for the team with which the content is associated.
   * This field is available only when the identity permission is requested in the manifest.
   */
  groupId?: string;

  /**
   * The Microsoft Teams ID for the team with which the content is associated.
   */
  teamId?: string;

  /**
   * Teamsite ID, aka sharepoint site id.
   */
  teamSiteId?: string;

  /**
   * The name for the team with which the content is associated.
   */
  teamName?: string;

  /**
   * The Microsoft Teams ID for the channel with which the content is associated.
   */
  channelId?: string;

  /**
   * The name for the channel with which the content is associated.
   */
  channelName?: string;

  /**
   * The type of the channel with which the content is associated.
   */
  channelType?: ChannelType;

  /**
   * The developer-defined unique ID for the entity this content points to.
   */
  entityId: string;

  /**
   * The developer-defined unique ID for the sub-entity this content points to.
   * This field should be used to restore to a specific state within an entity,
   * such as scrolling to or activating a specific piece of content.
   */
  subEntityId?: string;

  /**
   * The current locale that the user has configured for the app formatted as
   * languageId-countryId (for example, en-us).
   */
  locale: string;

  /**
   * More detailed locale info from the user's OS if available. Can be used together with
   * the @microsoft/globe NPM package to ensure your app respects the user's OS date and
   * time format configuration
   */
  osLocaleInfo?: ILocaleInfo;

  /**
   * @deprecated Use loginHint or userPrincipalName.
   * The UPN of the current user.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  upn?: string;

  /**
   * The Azure AD tenant ID of the current user.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  tid?: string;

  /**
   * The current UI theme.
   */
  theme?: string;

  /**
   * Indication whether the tab is in full-screen mode.
   */
  isFullScreen?: boolean;

  /**
   * The type of the team.
   */
  teamType?: TeamType;

  /**
   * The root SharePoint site associated with the team.
   */
  teamSiteUrl?: string;

  /**
   * The domain of the root SharePoint site associated with the team.
   */
  teamSiteDomain?: string;

  /**
   * The relative path to the SharePoint site associated with the team.
   */
  teamSitePath?: string;

  /**
   * The SharePoint my site domain associated with the user.
   */
  mySiteDomain?: string;

  /**
   * The SharePoint relative path to the current users mysite
   */
  mySitePath?: string;

  /**
   * The tenant ID of the host team.
   */
  hostTeamTenantId?: string;

  /**
   * The AAD group ID of the host team.
   */
  hostTeamGroupId?: string;

  /**
   * The relative path to the SharePoint folder associated with the channel.
   */
  channelRelativeUrl?: string;

  /**
   * Unique ID for the current Teams session for use in correlating telemetry data.
   */
  sessionId?: string;

  /**
   * Unique ID for the current app launch for use in correlating telemetry data.
   */
  launchId?: string;

  /**
   * The user's role in the team.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to the user's role, and never as proof of her role.
   */
  userTeamRole?: UserTeamRole;

  /**
   * The Microsoft Teams ID for the chat with which the content is associated.
   */
  chatId?: string;

  /**
   * A value suitable for use as a login_hint when authenticating with Azure AD.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  loginHint?: string;

  /**
   * The UPN of the current user. This may be an externally-authenticated UPN (e.g., guest users).
   * Because a malicious party run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  userPrincipalName?: string;

  /**
   * The Azure AD object id of the current user.
   * Because a malicious party run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  userObjectId?: string;

  /**
   * Indicates whether team is archived.
   * Apps should use this as a signal to prevent any changes to content associated with archived teams.
   */
  isTeamArchived?: boolean;

  /**
   * The name of the host client.
   */
  hostName: HubNames | string;

  /**
   * The type of the host client. Possible values are : android, ios, web, desktop, rigel
   */
  hostClientType?: HostClientType;

  /**
   * The features supported by the host that the app can query for via getContext. This allows the app to understand what capabilities the host has and adjust its behavior accordingly.
   */
  hostFeatures?: HostFeatures;

  /**
   * An array representing the hierarchy of ancestor hosts that the app is embedded inside of.
   * The array is ordered from immediate parent to root host.
   * For example, if Bizchat is running in Calendar in Teams, this would be ["Calendar", "Teams"].
   */
  hostAncestors?: string[];

  /**
   * The context where tab url is loaded (content, task, setting, remove, sidePanel)
   */
  frameContext?: FrameContexts;

  /**
   * The surface where app is loaded.
   * This will replace the frameContext in future.
   */
  renderingSurface?: RenderingSurfaces;

  /**
   * SharePoint context. This is only available when hosted in SharePoint.
   */
  sharepoint?: any;

  /**
   * The type of license for the current users tenant.
   */
  tenantSKU?: string;

  /**
   * The license type for the current user.
   */
  userLicenseType?: string;

  /**
   * The ID of the message from which this task module was launched.
   * This is only available in task modules launched from bot cards.
   */
  messageId?: string;

  /**
   * The ID of the parent message from which this task module was launched.
   * This is only available in task modules launched from bot cards.
   */
  parentMessageId?: string;

  /**
   * Current ring ID
   */
  ringId?: string;

  /**
   * Unique ID for the current session for use in correlating telemetry data.
   */
  appSessionId?: string;

  /**
   * Unique ID for the current app launch for use in correlating telemetry data.
   */
  appLaunchId?: string;

  /**
   * Represents whether calling is allowed for the current logged in User
   */
  isCallingAllowed?: boolean;

  /**
   * Represents whether PSTN calling is allowed for the current logged in User
   */
  isPSTNCallingAllowed?: boolean;

  /**
   * Meeting Id used by tab when running in meeting context
   */
  meetingId?: string;

  /**
   * The OneNote section ID that is linked to the channel.
   */
  defaultOneNoteSectionId?: string;

  /**
   * Indication whether the tab is in a pop out window
   */
  isMultiWindow?: boolean;

  /**
   * Indicates whether the page is being loaded in the background as part of an opt-in
   * performance enhancement. Background loading may be subject to special handling and/or
   * API restrictions by the hosting app. It is not enforced by the metaOS SDK.
   */
  isBackgroundLoad?: boolean;

  /**
   * Personal app icon y coordinate position
   */
  appIconPosition?: number;

  /**
   * Source origin from where the tab is opened
   */
  sourceOrigin?: string;

  /**
   * Time when the user clicked on the tab
   */
  userClickTime?: number;

  /**
   * Monotonic timestamp for when user clicked on the tab.
   */
  userClickTimeV2?: number;

  /**
   * Team Template ID if there was a Team Template associated with the creation of the team.
   */
  teamTemplateId?: string;

  /**
   * Where the user prefers the file to be opened from by default during file open
   */
  userFileOpenPreference?: FileOpenPreference;

  /**
   * The address book name of the current user.
   */
  userDisplayName?: string;

  /**
   * The dialog parameters that needs to return to the dialog in case dialog is opened by M365 for an ActionME
   */
  dialogParameters?: Record<string, string>;

  /**
   * App id that is used by Hubs to distinguish between different apps sideloaded or in store
   */
  appId: string;

  /**
   * The version of the manifest that the app is running.
   */
  manifestVersion?: string;
}

/**
 * The hydrated host info includes all the properties of AppHostInfo plus the optional features property that provides information about the host's supported features. This is used to provide a more complete context to the app about the host environment it is running in.
 */
export type HydratedHostInfo = AppHostInfo & {
  /**
   * The features supported by the host that the app can query for via getContext. This allows the app to understand what capabilities the host has and adjust its behavior accordingly.
   */
  features: HostFeatures;
};

/**
 * The modified keys for app information for the hydration step.
 */
export type ModifiedAppInfo = {
  /**
   * The app ID provided by the host.
   */
  appId?: string;

  /**
   * The version of the manifest that the app is running.
   */
  manifestVersion?: string;

  /**
   * Information about the host, including its features. This is used to provide the app with a more comprehensive understanding of the host environment when it receives the app context.
   */
  host: HydratedHostInfo;
};

/**
 * Utility type to modify properties of a type T with properties of another type R. It creates a new type by omitting the properties of T that are also in R, and then intersecting it with R. This is useful for creating new types based on existing ones while changing specific properties.
 */
export type Modify<T, R> = Omit<T, keyof R> & R;

/**
 * The hydrated app info includes all the properties of AppInfo but replaces the host property with the HydratedHostInfo type, which includes the additional features information about the host. This allows the app to have a more comprehensive understanding of the host environment when it receives the app context.
 */
export type HydratedAppInfo = Modify<AppInfo, ModifiedAppInfo>;

/**
 * Type of getContext struct in AppSDK.
 * It is used for placeholding context information
 */
export interface AppContext extends IWebContentContext {
  /**
   * Info of the user
   */
  user: UserInfo;
}

/**
 * The hydrated app context with all information the Hub SDK hydrates the context.
 */
export type HydratedAppContext<T> = Modify<
  T,
  {
    app: HydratedAppInfo;

    /**
     * A flag to indicate that the context has been hydrated with additional information such as host features. This can be used by the app to determine if it has access to the enriched context or if it is still working with a basic context.
     */
    readonly __hydrated: true;
  }
>;

/**
 * Materialized host information to be sent to the app.
 */
export interface MaterializedHostInfo extends AppHostInfo {
  /**
   * The name of the host serialized to a string.
   */
  name: string;
}

/**
 * Materialized app information to be sent to the app.
 */
export interface MaterializedAppInfo extends AppInfo {
  /**
   * Information about the host.
   */
  host: MaterializedHostInfo;
}

/**
 * App context prepared to be emitted back to the app.
 */
export type PreparedAppContext<
  T extends AppContext | IWebContentContext | HydratedAppContext<AppContext> | HydratedAppContext<IWebContentContext>,
> = Omit<T, '__hydrated'> & {
  /**
   * Info about the app
   */
  app: MaterializedAppInfo;
};

export type UserInfo = UserProfile & UserDetailsInfo;

/**
 * The serialized information about the app host.
 */
export interface SerializedHostInfo extends AppHostInfo {
  /**
   * The name of the host serialized to a string.
   */
  name: string;
}

/**
 * Serialized information about the app.
 */
export interface SerializedAppInfo extends AppInfo {
  /**
   * Information about the host.
   */
  host: SerializedHostInfo;
}

/**
 * App context serialized to be sent over postMessage.
 */
export type SerializedContext<T extends AppContext | IWebContentContext> = T & {
  /**
   * Info about the app
   */
  app: SerializedAppInfo;
};

export interface IBaseContext {
  /**
   * Info about the app
   */
  app: AppInfo;

  /**
   * Info about the user
   */
  user: UserDetailsInfo;
}

/**
 * Type to ingest context related information from Hubs.
 */
export interface IWebContentContext extends IBaseContext {
  /**
   * Info about the current page of App
   */
  page: PageInfo;

  /**
   * Common information applicable to all content actions
   */
  actionInfo?: ActionInfo;

  /**
   * Info about the Microsoft Teams channel
   */
  channel?: ChannelInfo;

  /**
   * Info about the Microsoft Teams chat
   */
  chat?: ChatInfo;

  /**
   * Info about the Microsoft Teams meeting
   */
  meeting?: MeetingInfo;

  /**
   * SharePoint context. This is only available when hosted in SharePoint.
   */
  sharepoint?: any;

  /**
   * Info about the sharePoint site associated with the team.
   */
  sharePointSite?: SharePointSiteInfo;

  /**
   * Info about the Microsoft Teams team
   */
  team?: TeamInfo;

  /**
   * Dialog prepopulation parameters that needs to return to the dialog in case dialog is opened by M365 for an ActionME
   * These parameters are provided to the hosts in ExternalAppCommands.processActionCommand call
   */
  dialogParameters?: Record<string, string>;
}

/**
 * Type to ingest hosted app information from Hubs.
 */
export interface AppInfo extends IHostContextInfo {
  /**
   * Personal app icon y coordinate position
   */
  iconPositionVertical?: number;

  /**
   * Time when the user clicked on the tab
   */
  userClickTime?: number;

  /**
   * Monotonic timestamp for when user clicked on the tab.
   */
  userClickTimeV2?: number;

  /**
   * The ID of the message from which this task module was launched.
   * This is only available in task modules launched from bot cards.
   */
  messageId?: string;

  /**
   * The ID of the parent message from which this task module was launched.
   * This is only available in task modules launched from bot cards.
   */
  parentMessageId?: string;

  /**
   * Where the user prefers the file to be opened from by default during file open
   */
  userFileOpenPreference?: FileOpenPreference;
}

/**
 * Type to ingest context related information from Hubs.
 * This is a subset of `AppInfo` without app specific parameters.
 */
export interface IHostContextInfo {
  /**
   * The current locale that the user has configured for the app formatted as
   * languageId-countryId (for example, en-us).
   */
  locale: string;

  /**
   * The current UI theme.
   */
  theme: Themes;

  /**
   * Unique ID for the current session for use in correlating telemetry data.
   */
  sessionId: string;

  /**
   * Unique ID for the current app launch for use in correlating telemetry data.
   */
  launchId?: string;

  /**
   * Info of the host
   */
  host: AppHostInfo;

  /**
   * More detailed locale info from the user's OS if available. Can be used together with
   * the @microsoft/globe NPM package to ensure your app respects the user's OS date and
   * time format configuration
   */
  osLocaleInfo?: ILocaleInfo;

  /**
   * Time when the user clicked on the tab
   */
  userClickTime?: number;

  /**
   * Monotonic timestamp for when user clicked on the tab.
   */
  userClickTimeV2?: number;
}

export interface AppHostInfo {
  /**
   * Indicates the currently running host.
   * During prototyping/onboarding, you can pass your host name as a string
   * As part of a full integration, you should add your new host name to the `HubNames` enum
   */
  name: AvailableHubNames;

  /**
   * The type of the host client. Possible values are : android, ios, web, desktop, rigel
   */
  clientType: HostClientType;

  /**
   * Unique ID for the current Host session for use in correlating telemetry data.
   */
  sessionId: string;

  /**
   * Current ring ID
   */
  ringId?: string;

  /**
   * Hub version
   */
  version: string;

  /**
   * An array representing the hierarchy of ancestor hosts that the app is embedded inside of.
   * The array is ordered from immediate parent to root host.
   * This allows apps to understand the full context of their embedding hierarchy.
   */
  ancestors?: string[];
}

export interface ChannelInfo {
  /**
   * The Microsoft Teams ID for the channel with which the content is associated.
   */
  id: string;

  /**
   * The name for the channel with which the content is associated.
   */
  displayName?: string;

  /**
   * The relative path to the SharePoint folder associated with the channel.
   */
  relativeUrl?: string;

  /**
   * The type of the channel with which the content is associated.
   */
  membershipType?: ChannelType;

  /**
   * The OneNote section ID that is linked to the channel.
   */
  defaultOneNoteSectionId?: string;

  /**
   * The tenant ID of the team which owns the channel.
   */
  ownerTenantId?: string;

  /**
   * The AAD group ID of the team which owns the channel.
   */
  ownerGroupId?: string;
}

export interface ChatInfo {
  /**
   * The Microsoft Teams ID for the chat with which the content is associated.
   */
  id: string;
}

export interface MeetingInfo {
  /**
   * Meeting Id used by tab when running in meeting context
   */
  id: string;
}

export enum ActionObjectType {
  M365Content = 'm365content',
}

export interface BaseActionObject<T extends ActionObjectType> {
  type: T;
}

export interface M365ContentAction extends BaseActionObject<ActionObjectType.M365Content> {
  /**
   * Only office content IDs are passed to the app. Apps should use these ids
   * to query the Microsoft graph for more details.
   */
  itemId: string;
  secondaryId?: SecondaryId;
}

export interface SecondaryId {
  name: SecondaryM365ContentIdName;
  value: string;
}

/**
 * These correspond with field names in the MSGraph
 */
export enum SecondaryM365ContentIdName {
  DriveId = 'driveId',
  GroupId = 'groupId',
  SiteId = 'siteId',
  UserId = 'userId',
}

/**
 * Common information applicable to all actions
 */
export interface ActionInfo {
  /**
   * Maps to the action id supplied inside the manifest
   */
  actionId: string;
  actionObjects: BaseActionObject<ActionObjectType>[];
}

export interface PageInfo {
  /**
   * The developer-defined unique ID for the page this content points to.
   */
  id: string;

  /**
   * The context where page url is loaded (content, task, setting, remove, sidePanel)
   */
  frameContext: FrameContexts;

  /**
   * The surface where the mos app is loaded.
   *
   * This will replace frameContext in the future. Currently, frameContext is used for two purposes:
   * 1. To indicate the context where the page is loaded (content, task, setting, remove, sidePanel)
   * 2. To gate the APIs that are only available in certain contexts (e.g. task, content)
   * In the future, teamsjs will depend on isSupported API to find out whether the capability is supported.
   * Hubs will be responsible for sending the correct services in the correct context.
   */
  renderingSurface?: RenderingSurfaces;

  /**
   * The developer-defined unique ID for the sub-page this content points to.
   * This field should be used to restore to a specific state within a page,
   * such as scrolling to or activating a specific piece of content.
   */
  subPageId?: string;

  /**
   * Indication whether the page is in full-screen mode.
   */
  isFullScreen?: boolean;

  /**
   * Indication whether the page is in a pop out window
   */
  isMultiWindow?: boolean;

  /**
   * Indicates whether the page is being loaded in the background as
   * part of an opt-in performance enhancement.
   */
  isBackgroundLoad?: boolean;

  /**
   * Source origin from where the page is opened
   */
  sourceOrigin?: string;

  /**
   * Contains information about configurable tab
   */
  config?: ConfigInfo;
}

export interface TeamInfo {
  /**
   * The Microsoft Teams ID for the team with which the content is associated.
   */
  internalId: string;

  /**
   * The name for the team with which the content is associated.
   */
  displayName?: string;

  /**
   * The type of the team.
   */
  type?: TeamType;

  /**
   * The Office 365 group ID for the team with which the content is associated.
   * This field is available only when the identity permission is requested in the manifest.
   */
  groupId?: string;

  /**
   * Indicates whether team is archived.
   * Apps should use this as a signal to prevent any changes to content associated with archived teams.
   */
  isArchived?: boolean;

  /**
   * Team Template ID if there was a Team Template associated with the creation of the team.
   */
  templateId?: string;

  /**
   * The user's role in the team.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to the user's role, and never as proof of her role.
   */
  userRole?: UserTeamRole;
}

export interface ConfigInfo {
  /**
   * ID for particular instance of selected tab.
   */
  instanceId: string;

  /**
   * The display name of the selected tab.
   */
  displayName: string;
}

export interface UserProfile {
  /**
   * The Azure AD object id of the current user.
   * Because a malicious party run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  id: string;

  /**
   * The address book name of the current user.
   */
  displayName?: string;

  /**
   * A value suitable for use as a login_hint when authenticating with Azure AD.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  loginHint?: string;

  /**
   * The UPN of the current user. This may be an externally-authenticated UPN (e.g., guest users).
   * Because a malicious party run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  userPrincipalName?: string;
}

export interface UserDetailsInfo {
  /**
   * Represents whether calling is allowed for the current logged in User
   */
  isCallingAllowed?: boolean;

  /**
   * Represents whether PSTN calling is allowed for the current logged in User
   */
  isPSTNCallingAllowed?: boolean;

  /**
   * The license type for the current user.
   */
  licenseType?: string;

  /**
   * The tenant related info of the current user.
   */
  tenant?: TenantInfo;

  /**
   * Data residency info of the current user.
   */
  dataResidency: DataResidency;
}

/**
 * Full set of Data Residencies supported by Hub SDK (and OTel)
 */
export enum DataResidency {
  /**
   * Public
   */
  Public = 'public',

  /**
   * European Union Data Boundary
   */
  EUDB = 'eudb',

  /**
   * US Gov DOD
   */
  DOD = 'dod',

  /**
   * US Gov DOJ
   */
  DOJ = 'doj',
}

/**
 * Limited set of Data Residencies information exposed to 1P application developers
 */
export enum DataResidencySlim {
  /**
   * Public
   */

  Public = 'public',

  /**
   * European Union Data Boundary
   */

  EUDB = 'eudb',

  /**
   * Other, stored to cover DoD and DoJ fields that will not be exposed
   */

  Other = 'other',
}

export interface TenantInfo {
  /**
   * The Azure AD tenant ID of the current user.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  id: string;

  /**
   * The type of license for the current users tenant.
   */
  sku?: string;
}

export interface SharePointSiteInfo {
  /**
   * The root SharePoint site associated with the team.
   */
  teamSiteUrl?: string;

  /**
   * The domain of the root SharePoint site associated with the team.
   */
  teamSiteDomain?: string;

  /**
   * The relative path to the SharePoint site associated with the team.
   */
  teamSitePath?: string;

  /**
   * The SharePoint my site domain associated with the user.
   */
  mySiteDomain?: string;

  /**
   * The SharePoint relative path to the current users mysite
   */
  mySitePath?: string;

  /**
   * Teamsite ID, aka sharepoint site id.
   */
  teamSiteId?: string;
}

/**
 * Enum attribute for registerFocusEnterHandler APIs that allows developers to send
 * more nuanced information about where focus should go in their app
 */
export enum EnterFocusType {
  /**
   * Determines the previous direction to focus in app when hot keys entered.
   */
  PreviousLandmark = 0,
  /**
   * Determines the next direction to focus in app when hot keys entered.
   */
  NextLandmark = 1,
  /**
   * Determines if the focus should go to the particular content of the app.
   * Read - Focus should go to the content of the app.
   */
  Read = 2,
  /**
   * Determines if the focus should go to the particular content of the app.
   * Compose - Focus should go to the compose area (such as textbox) of the app.
   */
  Compose = 3,
}

/**
 * Return focus action items
 */
export enum ReturnFocusType {
  /**
   * Determines the direction to focus in host for previous landmark.
   */
  PreviousLandmark = 0,
  /**
   * Determines the direction to focus in host for next landmark.
   */
  NextLandmark = 1,
  /**
   * Determines if the focus should go to the left rail of teams
   */
  GoToActivityFeed = 2,
}
